import React, { useState } from 'react';
import { Text } from '../text';
import { Badge } from '../badge';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/20/solid';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table';
import ContratoFianzasPanel from './ContratoFianzasPanel';
import { ESTADO_FIANZA, ensureFacturas, getEndososAbiertos } from '../../utils/fianzaEventos';
import { getPrimaAmount, getFacturasVigentes } from '../../utils/calculations';

const formatMonto = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

const oldestOverdueLabel = (dueDate) => {
  if (dueDate == null) return null;
  const days = Math.floor((Date.now() - dueDate) / 86400000);
  if (days <= 0) return null;
  return `la más atrasada: ${days} día${days !== 1 ? 's' : ''}`;
};

// Reshape the proveedor aggregate into the per-contrato rollup that
// ContratoFianzasPanel consumes, so the Proveedores drill-down is the exact
// same panel the Contratos and Proyectos tabs open. Prima / pago / endosos are
// only derived for the broker — the client portal must not see them.
const buildContratoStats = (emitidas, pendientes, isBroker) => ({
  rows: emitidas.map((f) => {
    const status = f._status;
    if (!isBroker) {
      return { fianza: f, status, prima: 0, pagoEstado: 'pendiente', pagadas: 0, facturasTotal: 0, endososPendientes: 0 };
    }
    const facturasV = getFacturasVigentes(ensureFacturas(f));
    const pagadas = facturasV.filter((fc) => fc.pagada).length;
    const pagoEstado =
      facturasV.length > 0 && pagadas === facturasV.length
        ? 'pagada'
        : pagadas > 0
        ? 'parcial'
        : 'pendiente';
    const abiertos = status?.estado === ESTADO_FIANZA.ACTIVA ? getEndososAbiertos(f) : [];
    return {
      fianza: f,
      status,
      prima: getPrimaAmount(f) || 0,
      pagoEstado,
      pagadas,
      facturasTotal: facturasV.length,
      endososPendientes: abiertos.length,
    };
  }),
  pendientes,
});

const ProveedorCard = ({
  data,
  mode = 'client',
  defaultExpanded = true,
  onContractClick,
  onAction,
}) => {
  const [collapsed, setCollapsed] = useState(!defaultExpanded);
  const [expandedContratos, setExpandedContratos] = useState(() => new Set());

  const isBroker = mode === 'broker';

  const toggleContrato = (contractId) => {
    setExpandedContratos((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) next.delete(contractId);
      else next.add(contractId);
      return next;
    });
  };

  // Group the proveedor's items by contrato — one table row per contrato,
  // expanding into the shared panel.
  const itemsByContract = new Map();
  const ensureContract = (item) => {
    if (!itemsByContract.has(item.contractId)) {
      itemsByContract.set(item.contractId, {
        contractId: item.contractId,
        contractNumero: item.contractNumero,
        contractTitle: item.contractTitle,
        proyectoNombre: item.proyectoNombre,
        pendientes: [],
        emitidas: [],
      });
    }
    return itemsByContract.get(item.contractId);
  };
  data.pendientes.forEach((p) => ensureContract(p).pendientes.push(p));
  data.emitidas.forEach((e) => ensureContract(e).emitidas.push(e));

  const contratos = Array.from(itemsByContract.values()).map((c) => {
    const counts = { vigente: 0, vencida: 0, terminada: 0, porEmitir: 0, futura: 0 };
    let monto = 0;
    c.emitidas.forEach((f) => {
      if (f._status?.estado !== ESTADO_FIANZA.ACTIVA) {
        counts.terminada++;
        return;
      }
      monto += Number(f.montoAfianzado) || 0;
      if (f._status.code === 'vigente') counts.vigente++;
      else if (f._status.code === 'vencida') counts.vencida++;
    });
    c.pendientes.forEach((fr) => {
      monto += Number(fr.monto) || 0;
      if (fr.isFutura) counts.futura++;
      else counts.porEmitir++;
    });
    return { ...c, counts, monto };
  });

  const oldestLabel = data.counts.overdue > 0 ? oldestOverdueLabel(data.oldestOverdueDate) : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-azul-surelia/20 overflow-hidden">
      <div className="px-4 py-3 bg-azul-surelia/5 border-b border-azul-surelia/10 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2.5 min-w-0 text-left"
          title={collapsed ? 'Ver contratos' : 'Ocultar contratos'}
        >
          {collapsed
            ? <ChevronRightIcon className="w-4 h-4 text-zinc-400 shrink-0" />
            : <ChevronDownIcon className="w-4 h-4 text-zinc-400 shrink-0" />}
          <BuildingOffice2Icon className="w-5 h-5 text-azul-surelia/70 shrink-0" />
          <span className="font-semibold text-azul-surelia truncate">{data.label}</span>
          {data.rfc && <span className="font-mono text-xs text-gray-500 shrink-0">{data.rfc}</span>}
          <Badge color="zinc">{contratos.length} contrato{contratos.length !== 1 ? 's' : ''}</Badge>
        </button>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="flex flex-wrap items-center gap-1">
            {data.counts.vigente > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                {data.counts.vigente} vig.
              </span>
            )}
            {data.counts.porEmitir > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                {data.counts.porEmitir} por emitir
              </span>
            )}
            {data.counts.vencida > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                {data.counts.vencida} venc. (cancelar)
              </span>
            )}
            {data.counts.futura > 0 && (
              <span
                className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-100 text-zinc-600"
                title="Sólo aplican al terminar el contrato (vicios ocultos)."
              >
                {data.counts.futura} futura{data.counts.futura !== 1 ? 's' : ''}
              </span>
            )}
            {oldestLabel && <span className="text-xs text-red-700">{oldestLabel}</span>}
          </div>
          <div
            className="text-right"
            title="Monto afianzado de las fianzas en vigor más el monto por afianzar de las requeridas."
          >
            <div className="text-sm font-bold text-azul-surelia tabular-nums">
              {formatMonto(data.montos.total)}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-400">afianzado + por emitir</div>
          </div>
        </div>
      </div>

      {!collapsed && (
        contratos.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                <TableRow>
                  <TableHeader className="text-white font-semibold w-8" />
                  <TableHeader className="text-white font-semibold">Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold">Proyecto</TableHeader>
                  <TableHeader
                    className="text-white font-semibold"
                    title="Monto afianzado de las fianzas en vigor más el monto por afianzar de las requeridas."
                  >Afianzado + por emitir</TableHeader>
                  <TableHeader className="text-white font-semibold">Fianzas</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {contratos.map((c, i) => {
                  const cExpanded = expandedContratos.has(c.contractId);
                  return (
                    <React.Fragment key={c.contractId}>
                      <TableRow
                        className={`cursor-pointer transition-colors hover:bg-azul-surelia/5 ${i % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                        onClick={() => onContractClick && onContractClick(c.contractId)}
                      >
                        <TableCell className="py-3 pr-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleContrato(c.contractId);
                            }}
                            className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title={cExpanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                          >
                            {cExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="text-sm font-medium text-azul-surelia">{c.contractNumero || '-'}</div>
                          {c.contractTitle && (
                            <div className="text-xs text-zinc-500 truncate max-w-[260px]" title={c.contractTitle}>
                              {c.contractTitle}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-sm">{c.proyectoNombre || '-'}</TableCell>
                        <TableCell className="py-3 text-sm tabular-nums text-azul-surelia font-semibold">
                          {c.monto > 0 ? formatMonto(c.monto) : '—'}
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex flex-wrap items-center gap-1">
                            {c.counts.vigente > 0 && (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">{c.counts.vigente} vig.</span>
                            )}
                            {c.counts.porEmitir > 0 && (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">{c.counts.porEmitir} por emitir</span>
                            )}
                            {c.counts.vencida > 0 && (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{c.counts.vencida} venc.</span>
                            )}
                            {c.counts.futura > 0 && (
                              <span
                                className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-100 text-zinc-600"
                                title="Sólo aplican al terminar el contrato (vicios ocultos)."
                              >
                                {c.counts.futura} futura{c.counts.futura !== 1 ? 's' : ''}
                              </span>
                            )}
                            {c.counts.terminada > 0 && (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">{c.counts.terminada} term.</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {cExpanded && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={5} className="py-3 px-6">
                            <ContratoFianzasPanel
                              stats={buildContratoStats(c.emitidas, c.pendientes, isBroker)}
                              contratoId={c.contractId}
                              onFianzaClick={onContractClick}
                              onSubir={onAction}
                              canUpload={isBroker}
                              mode={mode}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="px-4 py-6 text-center">
            <Text className="text-sm text-zinc-400">Sin contratos para este proveedor</Text>
          </div>
        )
      )}
    </div>
  );
};

export default ProveedorCard;
